import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  autoClose?: boolean;
}

interface ToastOptions {
  duration?: number;
  autoClose?: boolean;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, type?: ToastType, options?: ToastOptions | number) => void;
  success: (message: string, options?: ToastOptions | number) => void;
  error: (message: string, options?: ToastOptions | number) => void;
  warning: (message: string, options?: ToastOptions | number) => void;
  info: (message: string, options?: ToastOptions | number) => void;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ 
  children, 
  maxToasts = 5 
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', options?: ToastOptions | number) => {
      const id = `toast-${++toastIdRef.current}-${Date.now()}`;
      
      // Handle both number (duration) and options object for backwards compatibility
      const opts: ToastOptions = typeof options === 'number' 
        ? { duration: options, autoClose: true }
        : { duration: 4000, autoClose: true, ...options };
      
      const duration = opts.duration ?? 4000;
      const autoClose = opts.autoClose ?? true;
      
      const newToast: Toast = {
        id,
        message,
        type,
        duration,
        autoClose,
      };

      setToasts((prev) => {
        // Remove oldest toast if max reached
        const updatedToasts = prev.length >= maxToasts ? prev.slice(1) : prev;
        return [...updatedToasts, newToast];
      });

      // Auto-remove after duration (only if autoClose is enabled)
      if (autoClose && duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [maxToasts, removeToast]
  );

  const success = useCallback(
    (message: string, options?: ToastOptions | number) => showToast(message, 'success', options),
    [showToast]
  );

  const error = useCallback(
    (message: string, options?: ToastOptions | number) => showToast(message, 'error', options),
    [showToast]
  );

  const warning = useCallback(
    (message: string, options?: ToastOptions | number) => showToast(message, 'warning', options),
    [showToast]
  );

  const info = useCallback(
    (message: string, options?: ToastOptions | number) => showToast(message, 'info', options),
    [showToast]
  );

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider
      value={{
        toasts,
        showToast,
        success,
        error,
        warning,
        info,
        removeToast,
        clearAll,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};
